import { CreateCandidatoDto } from '@/api.types';

/** Formulário público e persistência usam um único campo "Nome completo" em `pessoa.nome`. */
export function normalizeNomeCompleto(full: string): string {
  return full.trim();
}

export type CreateCandidatoUiDto = Omit<CreateCandidatoDto, 'telefone' | 'celular' | 'endereco'> & {
  emailConfirmacao?: string;
  telefone?: { id?: number; ddd?: string; numero?: string };
  celular?: { id?: number; ddd?: string; numero?: string };
  endereco?: Omit<NonNullable<CreateCandidatoDto['endereco']>, 'numero'> & {
    numero?: number;
  };
};

export function getFieldName(fieldPath: string): string {
  const fieldNames: { [key: string]: string } = {
    nome: 'Nome Completo',
    cpf: 'CPF',
    dataNascimento: 'Data de Nascimento',
    genero: 'Gênero',
    email: 'E-mail',
    emailConfirmacao: 'Confirme o E-mail',
    'celular.ddd': 'DDD do Celular',
    'celular.numero': 'Número do Celular',
    'endereco.cep': 'CEP',
    'endereco.logradouro': 'Logradouro',
    'endereco.numero': 'Número',
    'endereco.complemento': 'Complemento',
    'endereco.bairro': 'Bairro',
    'endereco.cidade': 'Cidade',
    'endereco.estado': 'UF',
    instituicaoEnsinoId: 'Instituição de Ensino',
    instituicaoEnsinoNome: 'Nome da Instituição de Ensino',
    periodoAtualCurso: 'Período Atual do Curso',
    anoInicioCurso: 'Ano de Início do Curso',
    cursoPretendidoId: 'Curso Pretendido',
    municipio: 'Município',
    raca: 'Identificação Racial',
    optaVagasNegros: 'Opta por concorrer às vagas reservadas',
    pessoaComDeficiencia: 'Pessoa com deficiência?',
    optaVagasPcd: 'Opta por concorrer às vagas reservadas para pessoa com deficiência?',
    descricaoDeficiencia: 'Descrição da deficiência',
    documentoIdentificacaoUrl: 'Documento de Identificação (Oficial)',
    documentoEstudoUrl: 'Histórico Escolar (Graduação)',
    provaUrl: 'Prova (PDF)',
    comprovanteResidenciaUrl: 'Comprovante de Residência',
    laudoMedicoUrl: 'Laudo Médico (PCD)',
    documentacaoIndigenaUrl: 'Documentação Indígena',
    declaracaoVeracidade: 'Declaração de veracidade das informações prestadas',
    confirmacaoEdital: 'Confirmação de ciência e concordância com o edital do processo seletivo',
    consentimentoLgpd: 'Consentimento para tratamento de dados pessoais (LGPD)',
  };

  return fieldNames[fieldPath] || fieldPath;
}

export function getFieldErrorMessage(fieldPath: string): string {
  const fieldMessages: { [key: string]: string } = {
    nome: 'O campo "Nome Completo" é obrigatório. Por favor, preencha este campo.',
    cpf: 'O campo "CPF" é obrigatório. Por favor, preencha este campo.',
    dataNascimento: 'O campo "Data de Nascimento" é obrigatório. Por favor, preencha este campo.',
    genero: 'O campo "Gênero" é obrigatório. Por favor, selecione uma opção.',
    email: 'O campo "E-mail" é obrigatório. Por favor, preencha este campo.',
    emailConfirmacao:
      'O campo "Confirme o E-mail" é obrigatório. Por favor, preencha este campo ou verifique se os e-mails coincidem.',
    'celular.ddd': 'O campo "DDD do Celular" é obrigatório. Por favor, preencha este campo.',
    'celular.numero': 'O campo "Número do Celular" é obrigatório. Por favor, preencha este campo.',
    'endereco.cep': 'O campo "CEP" é obrigatório. Por favor, preencha este campo.',
    'endereco.logradouro': 'O campo "Logradouro" é obrigatório. Por favor, preencha este campo.',
    'endereco.numero': 'O campo "Número" é obrigatório. Por favor, preencha este campo.',
    'endereco.complemento': 'O campo "Complemento" é obrigatório. Por favor, preencha este campo.',
    'endereco.bairro': 'O campo "Bairro" é obrigatório. Por favor, preencha este campo.',
    'endereco.cidade': 'O campo "Cidade" é obrigatório. Por favor, preencha este campo.',
    'endereco.estado': 'O campo "UF" é obrigatório. Por favor, preencha este campo.',
    instituicaoEnsinoId:
      'O campo "Instituição de Ensino" é obrigatório. Por favor, selecione uma opção ou escolha "Outros".',
    instituicaoEnsinoNome: 'O campo "Nome da Instituição de Ensino" é obrigatório. Por favor, preencha este campo.',
    periodoAtualCurso: 'O campo "Período Atual do Curso" é obrigatório. Por favor, preencha este campo.',
    anoInicioCurso: 'O campo "Ano de Início do Curso" é obrigatório. Por favor, preencha este campo.',
    cursoPretendidoId: 'O campo "Curso Pretendido" é obrigatório. Por favor, selecione uma opção.',
    municipio: 'O campo "Município" é obrigatório. Por favor, selecione uma opção.',
    raca: 'O campo "Identificação Racial" é obrigatório. Por favor, selecione uma opção.',
    optaVagasNegros: 'O campo "Opta por concorrer às vagas reservadas" é obrigatório. Por favor, selecione uma opção.',
    pessoaComDeficiencia: 'O campo "Pessoa com deficiência?" é obrigatório. Por favor, selecione uma opção.',
    optaVagasPcd:
      'O campo "Opta por concorrer às vagas reservadas para pessoa com deficiência?" é obrigatório. Por favor, selecione uma opção.',
    descricaoDeficiencia: 'O campo "Descrição da deficiência" é obrigatório. Por favor, preencha este campo.',
    documentoIdentificacaoUrl:
      'O campo "Documento de Identificação (Oficial)" é obrigatório. Por favor, anexe o documento.',
    documentoEstudoUrl: 'O campo "Histórico Escolar (Graduação)" é obrigatório. Por favor, anexe o documento.',
    provaUrl: 'O campo "Prova (PDF)" é obrigatório. Por favor, anexe a prova.',
    comprovanteResidenciaUrl: 'O campo "Comprovante de Residência" é obrigatório. Por favor, anexe o documento.',
    laudoMedicoUrl: 'O campo "Laudo Médico (PCD)" é obrigatório. Por favor, anexe o documento.',
    documentacaoIndigenaUrl: 'O campo "Documentação Indígena" é obrigatório. Por favor, anexe o documento.',
    declaracaoVeracidade:
      'A "Declaração de veracidade das informações prestadas" é obrigatória. Por favor, marque esta opção.',
    confirmacaoEdital:
      'A "Confirmação de ciência e concordância com o edital do processo seletivo" é obrigatória. Por favor, marque esta opção.',
    consentimentoLgpd:
      'O "Consentimento para tratamento de dados pessoais (LGPD)" é obrigatório. Por favor, marque esta opção.',
  };

  return fieldMessages[fieldPath] || `O campo "${fieldPath}" é obrigatório. Por favor, preencha este campo.`;
}

export type RequiredFieldError = { field: string; isEmailMismatch?: boolean };

export function validateRequiredFields(data: CreateCandidatoUiDto): RequiredFieldError[] {
  const errors: RequiredFieldError[] = [];

  if (!data.nome || data.nome.trim() === '') errors.push({ field: 'nome' });
  if (!data.cpf || data.cpf.trim() === '') errors.push({ field: 'cpf' });
  if (!data.dataNascimento) errors.push({ field: 'dataNascimento' });
  if (!data.genero) errors.push({ field: 'genero' });

  if (!data.email || data.email.trim() === '') errors.push({ field: 'email' });
  if (!data.emailConfirmacao || data.emailConfirmacao.trim() === '') errors.push({ field: 'emailConfirmacao' });
  if (data.email && data.emailConfirmacao && data.email !== data.emailConfirmacao) {
    errors.push({ field: 'emailConfirmacao', isEmailMismatch: true });
  }
  if (!data.celular?.ddd || (typeof data.celular.ddd === 'string' && data.celular.ddd.trim() === '')) {
    errors.push({ field: 'celular.ddd' });
  }
  if (!data.celular?.numero || data.celular.numero.trim() === '') errors.push({ field: 'celular.numero' });

  if (!data.endereco?.cep || data.endereco.cep.trim() === '') errors.push({ field: 'endereco.cep' });
  if (!data.endereco?.logradouro || data.endereco.logradouro.trim() === '') errors.push({ field: 'endereco.logradouro' });
  if (!data.endereco?.numero) errors.push({ field: 'endereco.numero' });
  if (!data.endereco?.complemento || data.endereco.complemento.trim() === '') errors.push({ field: 'endereco.complemento' });
  if (!data.endereco?.bairro || data.endereco.bairro.trim() === '') errors.push({ field: 'endereco.bairro' });
  if (!data.endereco?.cidade || data.endereco.cidade.trim() === '') errors.push({ field: 'endereco.cidade' });
  if (!data.endereco?.estado || data.endereco.estado.trim() === '') errors.push({ field: 'endereco.estado' });

  const instituicaoIdStr = String(data.instituicaoEnsinoId ?? '');
  const temOutrosComNome = data.instituicaoEnsinoNome != null && String(data.instituicaoEnsinoNome).trim() !== '';
  const instituicaoPreenchida = (instituicaoIdStr !== '' && instituicaoIdStr !== 'undefined') || temOutrosComNome;
  if (!instituicaoPreenchida) errors.push({ field: 'instituicaoEnsinoId' });
  if ((instituicaoIdStr === 'outros' || temOutrosComNome) && (!data.instituicaoEnsinoNome || data.instituicaoEnsinoNome.trim() === '')) {
    errors.push({ field: 'instituicaoEnsinoNome' });
  }
  if (!data.periodoAtualCurso) errors.push({ field: 'periodoAtualCurso' });
  if (!data.anoInicioCurso) errors.push({ field: 'anoInicioCurso' });

  if (!data.cursoPretendidoId) errors.push({ field: 'cursoPretendidoId' });
  if (!data.municipio || data.municipio.trim() === '') errors.push({ field: 'municipio' });

  if (!data.raca) errors.push({ field: 'raca' });
  if (data.raca !== 'branca' && data.raca !== 'amarela' && data.optaVagasNegros === undefined) {
    errors.push({ field: 'optaVagasNegros' });
  }

  if (data.pessoaComDeficiencia === undefined) errors.push({ field: 'pessoaComDeficiencia' });
  if (data.pessoaComDeficiencia === true) {
    if (data.optaVagasPcd === undefined) errors.push({ field: 'optaVagasPcd' });
    if (data.optaVagasPcd === true) {
      if (!data.descricaoDeficiencia || data.descricaoDeficiencia.trim() === '') errors.push({ field: 'descricaoDeficiencia' });
    }
  }

  const hasDocumentoIdentificacao =
    data.documentoIdentificacaoUrl &&
    ((data.documentoIdentificacaoUrl as unknown) instanceof File ||
      (typeof data.documentoIdentificacaoUrl === 'string' && data.documentoIdentificacaoUrl.trim() !== ''));
  if (!hasDocumentoIdentificacao) errors.push({ field: 'documentoIdentificacaoUrl' });

  const hasDocumentoEstudo =
    data.documentoEstudoUrl &&
    ((data.documentoEstudoUrl as unknown) instanceof File ||
      (typeof data.documentoEstudoUrl === 'string' && data.documentoEstudoUrl.trim() !== ''));
  if (!hasDocumentoEstudo) errors.push({ field: 'documentoEstudoUrl' });

  const hasComprovanteResidencia =
    data.comprovanteResidenciaUrl &&
    ((data.comprovanteResidenciaUrl as unknown) instanceof File ||
      (typeof data.comprovanteResidenciaUrl === 'string' && data.comprovanteResidenciaUrl.trim() !== ''));
  if (!hasComprovanteResidencia) errors.push({ field: 'comprovanteResidenciaUrl' });

  if (data.optaVagasPcd === true) {
    const hasLaudoMedico =
      data.laudoMedicoUrl &&
      ((data.laudoMedicoUrl as unknown) instanceof File ||
        (typeof data.laudoMedicoUrl === 'string' && data.laudoMedicoUrl.trim() !== ''));
    if (!hasLaudoMedico) errors.push({ field: 'laudoMedicoUrl' });
  }

  if (data.raca === 'indigena' && data.optaVagasNegros === true) {
    const hasDocumentacaoIndigena =
      data.documentacaoIndigenaUrl &&
      ((data.documentacaoIndigenaUrl as unknown) instanceof File ||
        (typeof data.documentacaoIndigenaUrl === 'string' && data.documentacaoIndigenaUrl.trim() !== ''));
    if (!hasDocumentacaoIndigena) errors.push({ field: 'documentacaoIndigenaUrl' });
  }

  if (!data.declaracaoVeracidade) errors.push({ field: 'declaracaoVeracidade' });
  if (!data.confirmacaoEdital) errors.push({ field: 'confirmacaoEdital' });
  if (!data.consentimentoLgpd) errors.push({ field: 'consentimentoLgpd' });

  return errors;
}

export function findAllErrors(obj: any, path = ''): string[] {
  const errorsList: string[] = [];
  for (const key in obj) {
    const currentPath = path ? `${path}.${key}` : key;
    if (obj[key]?.message) errorsList.push(currentPath);
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      errorsList.push(...findAllErrors(obj[key], currentPath));
    }
  }
  return errorsList;
}

export function buildValidationToastMessage(
  fields: string[],
  emailMismatch: boolean,
): string {
  let errorMessage = 'Por favor, preencha os seguintes campos obrigatórios:\n\n';

  if (fields.length > 0) {
    const msgs = fields.map((field) => `• ${getFieldErrorMessage(field)}`);
    errorMessage += msgs.join('\n');
  } else {
    errorMessage += '• Existem campos obrigatórios que precisam ser preenchidos';
  }

  if (emailMismatch) {
    errorMessage += `${fields.length > 0 ? '\n\n' : ''}• Os e-mails não coincidem`;
  }

  return errorMessage;
}

